/* ==========================================================================
   uniq-motion.js — additive scroll-motion layer (WD-01)
   --------------------------------------------------------------------------
   Progressive enhancement over the EXISTING homepage. Targets the theme's
   current classes (.uniq-h, .uniq-eyebrow, .uniq-step, ...) — no markup, copy,
   colour, or layout is changed. Motion is transform/opacity only and always
   animates TO the state the page already has.

   Taste rule (apple-motion-brief.md): motion reads as MEASUREMENT, not
   performance. Scrubbed and precise, no overshoot easings, nothing moves
   without scroll input. Below-fold elements are put into their start state by
   GSAP off-screen, so a script failure leaves the page fully visible.

   The HERO has NO motion by design — it is present immediately.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function bailToStatic() { root.classList.remove('uniq-motion-on'); }

  // When motion can't run (reduce-motion / no GSAP), rest every scroll counter
  // on its final value so the science module reads "30 mm  vs  3 mm" statically.
  if (reduce || !window.gsap || !window.ScrollTrigger) {
    var counters = document.querySelectorAll('[data-uniq-counter]');
    for (var i = 0; i < counters.length; i++) {
      var to = counters[i].getAttribute('data-uniq-to');
      if (to != null) counters[i].textContent = to;
    }
    bailToStatic();
    return;
  }

  var gsap = window.gsap;
  gsap.registerPlugin(window.ScrollTrigger);
  root.classList.add('uniq-motion-on');

  var EASE = 'power2.out';   // no overshoot — measured, not springy
  var DUR = 0.95;            // a touch slower so reveals are actually seen

  function all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  // Reveal a group of elements, staggered, when their trigger scrolls in.
  function revealGroup(els, opts) {
    els = els.filter(Boolean);
    if (!els.length) return;
    opts = opts || {};
    gsap.from(els, {
      opacity: 0,
      y: opts.y == null ? 22 : opts.y,
      duration: opts.duration == null ? DUR : opts.duration,
      ease: EASE,
      stagger: opts.stagger == null ? 0.14 : opts.stagger,
      scrollTrigger: {
        trigger: opts.trigger || els[0],
        start: opts.start || 'top 70%',
        once: true
      }
    });
  }

  // --- 1. HERO — intentionally no motion (present immediately). ------------

  // --- 2. GENERIC SECTION HEADINGS ----------------------------------------
  // Every uniq block: eyebrow -> heading -> sub, staggered on enter (slower).
  all('.uniq-block').forEach(function (block) {
    var head = [
      block.querySelector('.uniq-eyebrow'),
      block.querySelector('.uniq-h'),
      block.querySelector('.uniq-sub')
    ].filter(Boolean);
    if (head.length) revealGroup(head, { trigger: block, stagger: 0.18 });
  });

  // --- 2b. §2 "A closer look" — subtle staggered fade-in on enter --------
  // Icon + label + subtext rows are all visible; they just fade/rise in once.
  all('[data-motion-360]').forEach(function (sec) {
    var list = sec.querySelector('.home-360__highlights');
    revealGroup(all('.home-360-highlight', sec), { trigger: list || sec, stagger: 0.1, y: 16, start: 'top 75%' });
  });

  // --- 2c. §3 NEUTRAL POSITIONS — vertical timeline; light the middle one --
  // Each entry (dot + picture + text) scroll-snaps to centre; the entry whose
  // centre is nearest the viewport middle is the active one (lit; rest dimmed).
  all('[data-motion-positions]').forEach(function (sec) {
    var entries = all('.np-entry', sec);
    if (!entries.length) return;
    function setActive() {
      var mid = window.innerHeight / 2, best = 0, bestD = Infinity;
      entries.forEach(function (e, i) {
        var r = e.getBoundingClientRect();
        var d = Math.abs((r.top + r.height / 2) - mid);
        if (d < bestD) { bestD = d; best = i; }
      });
      entries.forEach(function (e, i) { e.classList.toggle('is-active', i === best); });
    }
    window.ScrollTrigger.create({
      trigger: sec, start: 'top bottom', end: 'bottom top',
      onUpdate: setActive, onEnter: setActive, onEnterBack: setActive,
      onLeave: setActive, onLeaveBack: setActive
    });
    setActive();
  });

  // --- 2d. HOME FITTING slogan — words fly up + fade in, one after another --
  // CSS parks each .home-fitting__word at opacity:0 / translateY when motion is
  // on; here we animate them to place, staggered, when the section scrolls in.
  all('[data-motion-slogan]').forEach(function (sec) {
    var words = all('.home-fitting__word', sec);
    if (!words.length) return;
    gsap.to(words, {
      opacity: 1, y: 0, duration: 0.7, ease: EASE, stagger: 0.2,
      scrollTrigger: { trigger: sec, start: 'top 72%', once: true }
    });
  });

  // --- 3. §4 SCIENCE precision — right figure counts 30 -> 3 on scroll -----
  // Left figure static; right figure scrubs from its start value down to
  // [data-uniq-to] across the section, holding on the final value. Mono
  // tabular numerals (set in CSS) mean no width jump as digits change.
  all('[data-uniq-precision]').forEach(function (mod) {
    var el = mod.querySelector('[data-uniq-counter]');
    if (!el) return;
    var from = parseFloat(el.getAttribute('data-uniq-from'));
    if (isNaN(from)) from = 30;
    var to = parseFloat(el.getAttribute('data-uniq-to'));
    if (isNaN(to)) to = 3;
    // The markup already renders the final value (`to`), so if this never runs
    // the figure still reads 3. Count from `from` down to `to` across the
    // section, and always settle on `to` once past it.
    window.ScrollTrigger.create({
      trigger: mod, start: 'top 85%', end: 'top 55%',
      onUpdate: function (self) {
        el.textContent = String(Math.round(from + (to - from) * self.progress));
      },
      onLeave: function () { el.textContent = String(to); },
      onLeaveBack: function () { el.textContent = String(to); }
    });
  });

  // --- 4. SCIENCE inline CTA (kept) ---------------------------------------
  all('.uniq-inline-cta').forEach(function (el) {
    revealGroup([el], { trigger: el, y: 18 });
  });

  // --- 5. HOW IT WORKS: steps stagger (slower) ----------------------------
  all('.uniq-steps').forEach(function (steps) {
    revealGroup(all('.uniq-step', steps), { trigger: steps, stagger: 0.22, start: 'top 70%' });
  });

  // --- 6. CUSTOMERS: cards stagger + spec bars grow (slower) --------------
  all('.uniq-customers-strip').forEach(function (strip) {
    revealGroup(all('.uniq-customer-card', strip), { trigger: strip, stagger: 0.18, start: 'top 70%' });
    all('.uniq-customer-spec-fill', strip).forEach(function (fill) {
      gsap.from(fill, {
        scaleX: 0, transformOrigin: 'left center', duration: 1.1, ease: EASE,
        scrollTrigger: { trigger: fill, start: 'top 78%', once: true }
      });
    });
  });

  // --- 7. ANATOMY: features stagger (slower, triggered on the feature list)-
  all('.uniq-anatomy-body').forEach(function (body) {
    var list = body.querySelector('.uniq-product-features') || body;
    revealGroup(all('.uniq-product-feature', body), { trigger: list, stagger: 0.2, start: 'top 70%' });
  });

  // --- 8. PARALLAX — removed. ---------------------------------------------

  // --- 9. Generic reveal for remaining uniq groups ------------------------
  ['.uniq-story', '.uniq-pullquote', '.uniq-final-cta', '.uniq-product-trio', '.uniq-spec-bar']
    .forEach(function (sel) {
      all(sel).forEach(function (block) {
        var kids = all(':scope > *', block).filter(function (n) {
          return n.offsetHeight > 0 && !n.matches('script,style,link');
        });
        if (kids.length) revealGroup(kids, { trigger: block, y: 18, stagger: 0.1 });
      });
    });

  // --- Shopify theme-editor + late layout safety --------------------------
  window.addEventListener('load', function () { window.ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:load', function () { window.ScrollTrigger.refresh(); });
  document.addEventListener('shopify:section:unload', function () { window.ScrollTrigger.refresh(); });
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { window.ScrollTrigger.refresh(); });
  }
})();
